import { LinkOutlined, SwapOutlined } from "@ant-design/icons"
import { type MenuItemType, type SubMenuType } from "antd/es/menu/interface"
import { type ColumnType } from "antd/es/table/interface"
import React from "react"

import Icon from "../../../../../Icon"
import { RowActionsMenu } from "../../../../../components/EntityTable/RowActionsMenu.tsx"
import {
  INACTIVE_REASONS,
  REASON_STATUSES,
  WON_REASON_KEY,
  canReactivate,
  retiredParkingReasons,
  type InactiveReasonKey
} from "../../../../Lead/constants/operational-states.ts"
import { type LeadListItem } from "../../../types/types.ts"

type Action = MenuItemType | SubMenuType

interface GetLeadsColumnActionsProps {
  deleteAllowed?: boolean
  editAllowed?: boolean
  onSelectRow: (
    record: LeadListItem,
    action: string,
    payload?: unknown
  ) => void
}

/** The reason a row already sits in, so Transition never offers a no-op
 * target — the backend rejects transitioning to the current reason anyway.
 * A row the crm/0037 migration has not rewritten still names its reason
 * through a retired overlay while `current_status` reads NEW. */
const currentReasons = (record: LeadListItem): Set<InactiveReasonKey> => {
  const reasons = new Set<InactiveReasonKey>(
    retiredParkingReasons(record?.operational_states)
  )
  for (const [reason, status] of Object.entries(REASON_STATUSES)) {
    if (record?.current_status === status) {
      reasons.add(reason as InactiveReasonKey)
    }
  }
  return reasons
}

export const getLeadsColumnActions = ({
  deleteAllowed,
  editAllowed,
  onSelectRow
}: GetLeadsColumnActionsProps): ColumnType<LeadListItem> => {
  const getItems = (record: LeadListItem): Action[] => {
    // Won/Converted is terminal — change_status already blocks every edit
    // to a converted lead, and nothing reverses is_project. There is
    // nothing for Restore/Transition/Delete/View to execute here; the
    // Notes column already links a converted row to its resulting project.
    if (record?.is_project) return []

    // View mirrors the ungated name-click Link, so unlike every action
    // below it carries no permission of its own.
    const menuItems: Action[] = [
      {
        key: "view",
        label: "View",
        icon: <LinkOutlined />,
        onClick: () => onSelectRow(record, "view")
      }
    ]

    const restorable =
      editAllowed &&
      canReactivate(record?.is_archived, record?.operational_states)

    // Do Not Contact is now restorable too — the page gates it behind its
    // own confirmation step (see LeadsInactivePage), not a backend refusal.
    if (restorable) {
      menuItems.push({
        key: "reactivate",
        label: "Reactivate",
        icon: <Icon type={"Restore"} />,
        onClick: () => onSelectRow(record, "reactivate")
      })
    }

    const excluded = currentReasons(record)
    const targets = INACTIVE_REASONS.filter(
      ({ state }) => state !== WON_REASON_KEY && !excluded.has(state)
    )
    if (restorable && targets.length > 0) {
      // A submenu inside the same "⋮" dropdown, not a second Dropdown — the
      // target list is exactly what the standalone trigger used to open.
      menuItems.push({
        key: "transition",
        label: "Move to a different reason",
        icon: <SwapOutlined />,
        children: targets.map(({ state, label }) => ({
          key: state,
          label,
          onClick: () => onSelectRow(record, "transition", { reason: state })
        }))
      })
    }

    if (deleteAllowed) {
      menuItems.push({
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <Icon type={"Delete"} />,
        onClick: () => onSelectRow(record, "delete")
      })
    }

    return menuItems
  }

  return {
    title: "Actions",
    // View has no permission gate, so unlike before, this column is never
    // hidden outright — a Won/Converted row is the only one with no menu.
    fixed: "right",
    width: 60,
    align: "right",
    render: (_, record) => {
      const items = getItems(record)

      if (!items.length) return null

      return <RowActionsMenu items={items} />
    }
  }
}
